import { useQuery } from '@apollo/react-hooks';

import { AccessStatus, useSessionData } from '@confluence/session-data';
import {
	useCanAddBillingForTrialProductUser,
	useCanUpgradeAndPayForProductUser,
} from '@confluence/experiment-more-product-users-can-upgrade-pay';
import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition';
import { markErrorAsHandled } from '@confluence/graphql';
import { SPAViewContext } from '@confluence/spa-view-context';

import { getTrialDaysLeft } from './getTrialDaysLeftFromQuery';
import type { EditionAwarenessDataType } from './DataType';
import { LicenseDetailsQuery } from './LicenceDetailsQuery.graphql';
import type { LicenseDetailsQuery as LicenseDetailsQueryType } from './__types__/LicenseDetailsQuery';
import { useContext } from 'react';

export const useEditionAwarenessDataQueries = (skip = false): EditionAwarenessDataType => {
	const { isSiteAdmin } = useContext(SPAViewContext);
	const { edition, cloudId, isLoggedIn, accessStatus } = useSessionData();
	const isLicensed =
		accessStatus === AccessStatus.LICENSED_ADMIN_ACCESS ||
		accessStatus === AccessStatus.LICENSED_USE_ACCESS;
	const shouldSkipQueries = skip || !cloudId || !isLoggedIn || !isLicensed;

	const shouldSkipQueriesForFreeEdition = shouldSkipQueries || edition !== ConfluenceEdition.FREE;
	const shouldSkipQueriesForTrialEdition = shouldSkipQueries || edition === ConfluenceEdition.FREE;

	// Revelant only to free edition
	const {
		canUpgradeAndPayForProductUser,
		isBillingAdmin: isBillingAdminFreeEdition,
		loading: isCanUpgradeAndPayLoading,
		error: canUpgradeAndPayError,
	} = useCanUpgradeAndPayForProductUser(shouldSkipQueriesForFreeEdition);

	// Revelant only to trial edition
	const {
		isBillingAdmin: isBillingAdminTrialEdition,
		hasBillingDetails,
		canAddBillingForTrial,
		isPreDunning,
		analyticsAttributes: canAddBillingForTrialAnalyticsAttributes,
		loading: isCanAddBillingLoading,
		commerceError: canAddBillingForTrialError,
	} = useCanAddBillingForTrialProductUser(shouldSkipQueriesForTrialEdition);
	const {
		data: licenseDetailsData,
		loading: isLicenseLoading,
		error: trialDetailsError,
	} = useQuery<LicenseDetailsQueryType>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
		LicenseDetailsQuery,
		{
			skip: shouldSkipQueriesForTrialEdition,
		},
	);

	const billingSourceSystem = licenseDetailsData?.license?.billingSourceSystem;
	const trialEndDate = licenseDetailsData?.license?.trialEndDate;
	const trialDaysLeft = getTrialDaysLeft(trialEndDate);
	const isInTrial = trialDaysLeft ? trialDaysLeft > 0 : false;
	const loading = isCanUpgradeAndPayLoading || isCanAddBillingLoading || isLicenseLoading;
	const errors = { canUpgradeAndPayError, canAddBillingForTrialError, trialDetailsError };

	if (canAddBillingForTrialError) {
		markErrorAsHandled(canAddBillingForTrialError);
	}

	return {
		isSiteAdmin,
		edition,
		billingSourceSystem,
		canAddBillingForTrialAnalyticsAttributes,
		canUpgradeAndPayInFreeEdition: canUpgradeAndPayForProductUser,
		isInTrial,
		trialDaysLeft,
		trialEndDate,
		isPreDunning,
		isBillingAdmin: isBillingAdminFreeEdition || isBillingAdminTrialEdition,
		hasBillingDetails,
		canAddBillingForTrial,
		shouldHideEditionAwareness: shouldSkipQueries,
		loading,
		errors,
	};
};
